import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@papier/ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@papier/ui/components/ui/card";
import { Badge } from "@papier/ui/components/ui/badge";
import { CheckIcon } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import type { SubscriptionPlan, SubscriptionPlanConfig } from "@papier/shared";
import { PRO_PLAN_PRICE } from "@/constants/pro-plan-price";

export const Route = createFileRoute("/_app/pricing")({
  component: PricingPage,
});

const plans = {
  free: {
    name: "Free",
    price: 0,
    description: "Perfect for getting started",
    features: ["Up to 3 projects", "Basic templates", "Email support", "Community access"],
    buttonText: "Get Started Free",
  },
  pro: {
    name: "Pro",
    price: PRO_PLAN_PRICE,
    description: "For professional teams",
    features: [
      "Unlimited projects",
      "Premium templates",
      "Priority support",
      "Advanced analytics",
      "Custom branding",
      "API access",
      "Team collaboration",
    ],
    buttonText: "Upgrade to Pro",
  },
} satisfies Record<SubscriptionPlan, SubscriptionPlanConfig>;

function PricingPage() {
  const { mutateAsync, isPending } = useMutation(
    trpc.stripe.createCheckoutSession.mutationOptions({
      onError: (error) => {
        toast.error("Failed to create checkout session", {
          description: error.message,
        });
      },
    }),
  );

  const handleSubscribe = async (plan: SubscriptionPlan) => {
    const { url } = await mutateAsync({ plan });

    if (url) {
      window.location.href = url;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br px-4 py-12">
      <div className="mx-auto max-w-6xl">
        <div className="mb-12 text-center">
          <h1 className="mb-4 text-4xl font-bold text-white">Choose Your Plan</h1>
          <p className="mx-auto max-w-2xl text-xl text-white">
            Start for free and upgrade as your needs grow. All plans include our core features.
          </p>
        </div>

        <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-2">
          {Object.entries(plans).map(([planKey, plan]) => (
            <Card
              key={planKey}
              className={`relative ${planKey === "pro" ? "scale-105 shadow-lg ring-2 ring-blue-500" : ""}`}
            >
              {planKey === "pro" && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 transform bg-blue-500">
                  Most Popular
                </Badge>
              )}

              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold">{plan.name}</CardTitle>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  {planKey === "pro" && <span className="text-white">/month</span>}
                </div>
                <CardDescription className="text-base">{plan.description}</CardDescription>
              </CardHeader>

              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
                      <span className="text-sm text-white">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter>
                <Button
                  className="w-full"
                  variant={planKey === "pro" ? "default" : "outline"}
                  onClick={() => handleSubscribe(planKey as SubscriptionPlan)}
                  disabled={isPending}
                >
                  {isPending ? "Loading..." : plan.buttonText}
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="mb-4 text-white">Questions about our plans?</p>
          <div className="flex justify-center gap-4">
            <Button variant="ghost" asChild>
              <Link to="/subscription">View Current Subscription</Link>
            </Button>
          </div>
        </div>

        <div className="mt-16 rounded-lg p-8 shadow-sm">
          <h2 className="mb-8 text-center text-2xl font-bold">Test Stripe Integration</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="rounded-lg p-6">
              <h3 className="mb-4 font-semibold">Free Plan Test</h3>
              <p className="mb-4 text-sm text-white">
                Test the free plan checkout flow. This will redirect to Stripe's test checkout.
              </p>
              <Button
                variant="outline"
                onClick={() => handleSubscribe("free")}
                disabled={isPending}
                className="w-full"
              >
                {isPending ? "Loading..." : "Test Free Plan"}
              </Button>
            </div>

            <div className="rounded-lg p-6">
              <h3 className="mb-4 font-semibold">Pro Plan Test</h3>
              <p className="mb-4 text-sm text-white">
                Test the pro plan checkout flow. Use test card: 4242 4242 4242 4242
              </p>
              <Button
                onClick={() => handleSubscribe("pro")}
                disabled={isPending}
                className="w-full"
              >
                {isPending ? "Loading..." : "Test Pro Plan"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
